import React, { useEffect, useState, createContext, useMemo } from 'react';
import { renderToString } from 'react-dom/server';
import mapboxgl from 'mapbox-gl';
import { useSettingsContext } from '@mono/platmap-settings-context';
import { LightningLayer, PlatmapLayerContext } from '@mono/types/src/platmap';
import { toHash } from '@mono/to-hash';
import { useLightningContext } from '@mono/lightning-context';
import { Tooltip } from '@mono/metui';
import { DateTime, Duration } from 'luxon';
import { generateLightningCircleGeojsons } from './LightningCircle';
import { featureCollection } from '@turf/helpers';
import nearestPoint from '@turf/nearest-point';
import { strikeLayer, circleLayer, circleOutlineLayer, circleOutlineWarningLayer, circleOutlineAlertLayer } from './map-configs';
import { useTimeContext } from '../time-context';

export const LightningServiceContext = createContext<PlatmapLayerContext>({
    setMap: () => {},
    shuffle: () => {},
});

interface LightningServiceContextProviderProps {
    children: JSX.Element;
    delimiter: string;
    anchorId: string;
}

// const popCircleAt = (map: mapboxgl.Map, lngLat: [number, number]) => {
//     [0, 150].forEach((delay) => {
//         setTimeout(() => {
//             const wrapper = document.createElement('div');
//             const ripple = document.createElement('div');

//             ripple.className = 'pop-circle';
//             wrapper.appendChild(ripple);

//             new mapboxgl.Marker(wrapper).setLngLat(lngLat).addTo(map);

//             setTimeout(() => wrapper.remove(), 1500);
//         }, delay);
//     });
// };

export const LightningServiceContextProvider = ({ children, delimiter, anchorId }: LightningServiceContextProviderProps) => {
    const [state, setState] = useState<{
        map: mapboxgl.Map | null;
        shuffleIndex: string;
    }>({
        map: null,
        shuffleIndex: 'The lightning shuffle index',
    });

    const { strikes, isLoading: isLoadingLightning, strikesKey, totalStrikesReceived, setQuery } = useLightningContext();

    const { selectedTime, now: nowISO, setIsLoading, updateNow } = useTimeContext();

    const { settings, setSettings } = useSettingsContext();
    const [layersHaveBeenSetup, setLayersHaveBeenSetup] = useState(false);
    const now = DateTime.fromISO(nowISO).setZone('utc');

    const [bbox, setBbox] = useState<[number, number, number, number]>();

    useEffect(() => {
        if (!state.map) return;

        // Set initial bounds when map is first available
        const bounds = state.map.getBounds();
        if (bounds && !bbox) {
            setBbox([bounds.getWest(), bounds.getSouth(), bounds.getEast(), bounds.getNorth()]);
        }

        const onMoveEnd = (e: mapboxgl.MapboxEvent) => {
            const bounds = state.map?.getBounds();
            if (!bounds) return;
            setBbox([bounds.getWest(), bounds.getSouth(), bounds.getEast(), bounds.getNorth()]);
        };

        state.map.on('moveend', onMoveEnd);
        return () => {
            if (!state.map) return;
            state.map.off('moveend', onMoveEnd);
        };
    }, [state.map, bbox]);

    const lightningLayer = settings.layers.find((l): l is { id: string; state: LightningLayer } => l.state.type === 'lightning');

    const isLive = useMemo(() => {
        if (settings.timeControl.type === 'livemode') return true;
        return false;
    }, [toHash(settings.timeControl)]);

    const timeOfWebsocketConnection = useMemo(() => DateTime.utc().toISO(), [isLive]);

    const timespan = useMemo(() => {
        if (!selectedTime) return;

        if (settings.timeControl.type === 'timeoffset') {
            return [DateTime.fromISO(selectedTime).setZone('utc').minus({ hours: 1 }).toISO({ suppressMilliseconds: true }), selectedTime].join('--');
        }
        if (settings.timeControl.type === 'livemode') {
            return [
                DateTime.fromISO(timeOfWebsocketConnection).setZone('utc').minus({ hours: 1 }).toISO({ suppressMilliseconds: true }),
                DateTime.fromISO(timeOfWebsocketConnection).setZone('utc').plus({ hours: 1 }).toISO({ suppressMilliseconds: true }),
            ].join('--');
        }

        const [earlyDuration, lateDuration] = settings.timeControl.timespan.split('--').map((d) => Duration.fromISO(d));

        const earlyTime = now.plus(earlyDuration).minus({ hours: 1 }).toISO({ suppressMilliseconds: true });
        const lateTime = now.plus(lateDuration).toISO({ suppressMilliseconds: true });
        return [earlyTime, lateTime].join('--');
    }, [toHash(settings.timeControl), nowISO, selectedTime, timeOfWebsocketConnection]);

    useEffect(() => {
        setIsLoading((prev) => {
            return {
                ...prev,
                lightning: isLoadingLightning,
            };
        });
    }, [isLoadingLightning]);

    // const prevStrikeIds = useRef<Set<string>>(new Set());

    useEffect(() => {
        if (!state.map) return;
        if (!layersHaveBeenSetup) return;

        const mapboxStyle = state.map.getStyle();
        const mapboxLayers = mapboxStyle.layers;

        if (!mapboxLayers) return;
        if (!lightningLayer) return;
        if (!state.map.getLayer(lightningLayer.id)) return;

        const anchorIndex = ['plat', 'lightning', lightningLayer.id, anchorId].join(delimiter);

        if (!mapboxLayers.find((l) => l.id === anchorIndex)) {
            setTimeout(() => {
                setState((prev) => ({ ...prev, shuffleIndex: prev.shuffleIndex + 'leaveMeAlone' }));
            }, 16);
            return;
        }

        const layerAnchorId = ['plat', 'lightning', lightningLayer.id, anchorId].join(delimiter);
        state.map.moveLayer(lightningLayer.id + '-circles', layerAnchorId);
        state.map.moveLayer(lightningLayer.id + '-circles-outline', layerAnchorId);
        state.map.moveLayer(lightningLayer.id + '-circles-outline-warning', layerAnchorId);
        state.map.moveLayer(lightningLayer.id + '-circles-outline-alert', layerAnchorId);
        state.map.moveLayer(lightningLayer.id, layerAnchorId);
    }, [state.shuffleIndex, toHash(lightningLayer), layersHaveBeenSetup]);

    useEffect(() => {
        if (settings.timeControl.type !== 'livemode') return;
        updateNow();
    }, [strikesKey, toHash(settings.timeControl)]);

    const strikesToRender = useMemo(() => {
        if (!selectedTime) return strikes;

        const earliestTime = DateTime.fromISO(selectedTime).setZone('utc').minus({ hours: 1 }).toISO();

        const strikesToRender = {
            ...strikes,
            features: strikes.features.filter((f) => {
                if (f.properties.dateTime > selectedTime) return false;
                if (f.properties.dateTime < earliestTime) return false;

                return true;
            }),
        };

        return strikesToRender;
    }, [selectedTime, strikesKey]);

    useEffect(() => {
        if (!state.map) return;
        if (!lightningLayer) return;
        if (!layersHaveBeenSetup) return;

        const source = state.map.getSource(lightningLayer.id);
        if (!source) return;
        if (source.type !== 'geojson') return;

        if (!selectedTime) return;

        state.map.setPaintProperty('lightning', 'icon-color', [
            'interpolate',
            ['linear'],
            ['number', ['-', DateTime.fromISO(selectedTime).toMillis() / 1000, ['get', 'unixTime']]],
            -0.001,
            'rgba(0, 0, 0, 0)',
            0,
            '#F10707',
            20 * 60,
            '#FFE600',
            40 * 60,
            '#39FF69',
            60 * 60,
            '#00A3FF',
            60 * 60 + 0.001,
            'rgba(0, 0, 0, 0)',
        ]);

        source.setData(strikesToRender);

        // --- Pop circles for new Transpower strikes ---
        // TOA implementation needs more work to be able to use this as it won't handle the
        // const currentIds = new Set(strikesToRender.features.filter((f) => f.properties.source === 'TRANSPOWER').map((f) => f.id));
        // const newIds = Array.from(currentIds).filter((id) => !prevStrikeIds.current.has(id));

        // newIds.forEach((id) => {
        //     const feat = strikesToRender.features.find((f) => f.id === id)!;
        //     popCircleAt(state.map!, feat.geometry.coordinates);
        // });

        // prevStrikeIds.current = currentIds;
    }, [strikesToRender.features.length, layersHaveBeenSetup]);

    useEffect(() => {
        if (!state.map || !lightningLayer) return;
        if (!selectedTime) return;
        if (!layersHaveBeenSetup) return;

        state.map.setPaintProperty('lightning', 'icon-color', [
            'interpolate',
            ['linear'],
            ['number', ['-', DateTime.fromISO(selectedTime).toMillis() / 1000, ['get', 'unixTime']]],
            -0.001,
            'rgba(0, 0, 0, 0)',
            0,
            '#F10707',
            20 * 60,
            '#FFE600',
            40 * 60,
            '#39FF69',
            60 * 60,
            '#00A3FF',
            60 * 60 + 0.001,
            'rgba(0, 0, 0, 0)',
        ]);
    }, [selectedTime, layersHaveBeenSetup]);

    useEffect(() => {
        if (!state.map) return;
        if (!lightningLayer) return;
        if (!layersHaveBeenSetup) return;
        if (!selectedTime) return;
        const lightningCirclesOutlineSource = state.map.getSource('lightning-circles-outline');
        if (!lightningCirclesOutlineSource || lightningCirclesOutlineSource.type !== 'geojson') return;
        const lightningCirclesSource = state.map.getSource('lightning-circles');
        if (!lightningCirclesSource || lightningCirclesSource.type !== 'geojson') return;
        const lightningCirclesOutlineWarningSource = state.map.getSource('lightning-circles-outline-warning');
        if (!lightningCirclesOutlineWarningSource || lightningCirclesOutlineWarningSource.type !== 'geojson') return;
        const lightningCirclesOutlineAlertSource = state.map.getSource('lightning-circles-outline-alert');
        if (!lightningCirclesOutlineAlertSource || lightningCirclesOutlineAlertSource.type !== 'geojson') return;

        const lightningCircles = lightningLayer.state.lightningCircles;

        if (lightningCircles.length === 0) {
            lightningCirclesOutlineSource.setData({ type: 'FeatureCollection', features: [] });
            lightningCirclesSource.setData({ type: 'FeatureCollection', features: [] });
            lightningCirclesOutlineWarningSource.setData({ type: 'FeatureCollection', features: [] });
            lightningCirclesOutlineAlertSource.setData({ type: 'FeatureCollection', features: [] });
            return;
        }

        const { segments, lines } = lightningCircles.reduce(
            (acc, lightningCirlce) => {
                const { segments, lines } = generateLightningCircleGeojsons(lightningCirlce, strikesToRender, selectedTime);

                return {
                    segments: [...acc.segments, ...segments],
                    lines: { ...acc.lines, ...lines },
                };
            },
            {
                segments: [] as GeoJSON.Feature<GeoJSON.LineString>[],
                lines: {} as Record<string, GeoJSON.Feature<GeoJSON.LineString>>,
            }
        );

        lightningCirclesSource.setData({
            type: 'FeatureCollection' as const,
            features: segments,
        });

        const noRiskLines = Object.values(lines).filter((l) => l.properties?.status === 0);
        lightningCirclesOutlineSource.setData({
            type: 'FeatureCollection' as const,
            features: noRiskLines,
        });

        const warningLines = Object.values(lines).filter((l) => l.properties?.status === 1);
        lightningCirclesOutlineWarningSource.setData({
            type: 'FeatureCollection' as const,
            features: warningLines,
        });

        const alertLines = Object.values(lines).filter((l) => l.properties?.status === 2);
        lightningCirclesOutlineAlertSource.setData({
            type: 'FeatureCollection' as const,
            features: alertLines,
        });
    }, [selectedTime, strikesToRender.features.length, layersHaveBeenSetup, toHash(lightningLayer?.state.lightningCircles)]);

    useEffect(() => {
        if (!state.map || !lightningLayer) return;
        if (layersHaveBeenSetup) return;

        if (!state.map.getSource(lightningLayer.id)) {
            state.map.addSource(lightningLayer.id, {
                type: 'geojson',
                data: {
                    type: 'FeatureCollection',
                    features: [],
                },
            });
        }

        if (!state.map.getSource(lightningLayer.id + '-circles-outline')) {
            state.map.addSource(lightningLayer.id + '-circles-outline', {
                type: 'geojson',
                data: {
                    type: 'FeatureCollection',
                    features: [],
                },
            });
        }

        if (!state.map.getSource(lightningLayer.id + '-circles-outline-warning')) {
            state.map.addSource(lightningLayer.id + '-circles-outline-warning', {
                type: 'geojson',
                data: {
                    type: 'FeatureCollection',
                    features: [],
                },
            });
        }

        if (!state.map.getSource(lightningLayer.id + '-circles-outline-alert')) {
            state.map.addSource(lightningLayer.id + '-circles-outline-alert', {
                type: 'geojson',
                data: {
                    type: 'FeatureCollection',
                    features: [],
                },
            });
        }

        if (!state.map.getSource(lightningLayer.id + '-circles')) {
            state.map.addSource(lightningLayer.id + '-circles', {
                type: 'geojson',
                data: {
                    type: 'FeatureCollection',
                    features: [],
                },
            });
        }

        ['cloud-cloud', 'cloud-ground-positive', 'cloud-ground-negative'].forEach((icon) => {
            if (!state.map) return;
            if (!state.map.hasImage(icon)) {
                //todo just use svg
                const img = new Image();
                img.src = `${window.location.origin}${window.location.pathname}${icon}.svg`;
                img.onload = () => {
                    if (!state.map) return;
                    state.map.addImage(icon, img, {
                        sdf: true,
                    });
                };
            }
        });

        state.map.addLayer({
            ...circleLayer,
            id: lightningLayer.id + '-circles',
            source: lightningLayer.id + '-circles',
        });

        state.map.addLayer({
            ...circleOutlineLayer,
            id: lightningLayer.id + '-circles-outline',
            source: lightningLayer.id + '-circles-outline',
        });

        state.map.addLayer({
            ...circleOutlineWarningLayer,
            id: lightningLayer.id + '-circles-outline-warning',
            source: lightningLayer.id + '-circles-outline-warning',
        });

        state.map.addLayer({
            ...circleOutlineAlertLayer,
            id: lightningLayer.id + '-circles-outline-alert',
            source: lightningLayer.id + '-circles-outline-alert',
        });

        state.map.addLayer({
            ...strikeLayer,
            id: 'lightning',
            source: lightningLayer.id,
        });

        const popup = new mapboxgl.Popup({
            maxWidth: '290px',
            closeButton: true,
            closeOnClick: false,
        });

        state.map.on('mousemove', 'lightning', (e) => {
            if (!state.map) return;
            state.map.getCanvas().style.cursor = 'pointer';

            if (!e.features) return;
            const closestPoint = nearestPoint([e.lngLat.lng, e.lngLat.lat], featureCollection(e.features as GeoJSON.Feature<GeoJSON.Point>[]));
            state.map.setLayoutProperty('lightning', 'icon-size', ['match', ['get', 'id'], closestPoint.properties.id, 2, 1]);
        });

        state.map.on('mouseleave', 'lightning', () => {
            if (!state.map) return;
            state.map.getCanvas().style.cursor = '';
            state.map.setLayoutProperty('lightning', 'icon-size', 1);
        });

        state.map.on('click', () => {
            if (!state.map) return;
            if (popup.isOpen()) {
                popup.remove();
            }
        });

        state.map.on('click', ['lightning'], (e) => {
            if (!state.map) return;
            if (popup.isOpen()) {
                popup.remove();
            }
            if (!e.features || !e.features[0]) return;
            if (!selectedTime) return;

            //@ts-ignore this is incorrect
            const coordinates = e.features[0].geometry.coordinates.slice();
            const kA = e.features[0].properties?.kA;

            while (Math.abs(e.lngLat.lng - coordinates[0]) > 180) {
                coordinates[0] += e.lngLat.lng > coordinates[0] ? 360 : -360;
            }

            const time = DateTime.fromISO(e.features[0].properties?.dateTime.replace('\r\n', ''));
            const formattedTime = `${time.toFormat('t')} (UTC ${time.setZone('UTC').toFormat('HH:mm')})`;

            const tooltip = Tooltip({
                title: 'Lightning Strike',
                content: [
                    { name: 'Time', value: formattedTime },
                    { name: 'Kiloamperes', value: kA ? kA.toFixed(2) + 'kA' : 'N/A' },
                    { name: 'Lat, Lon', value: `${coordinates[1].toFixed(3)}, ${coordinates[0].toFixed(3)}` },
                    { name: 'Type', value: `Cloud to ${e.features[0].properties?.strike_type.toLowerCase()}` },
                ],
                age: isLive
                    ? Date.now() - e.features[0].properties?.unixTime * 1000
                    : DateTime.fromISO(selectedTime).toMillis() - e.features[0].properties?.unixTime * 1000,
            });

            popup.setLngLat(coordinates).setHTML(renderToString(tooltip)).addTo(state.map);
        });

        state.map.on('moveend', () => {
            if (!state.map) return;
            const newBounds = state.map.getBounds();

            if (!setSettings) return;
            setSettings((prev) => {
                return { ...prev, maxBounds: newBounds };
            });
        });

        setLayersHaveBeenSetup(true);
    }, [state.map, toHash(lightningLayer), selectedTime]);

    useEffect(() => {
        setQuery({
            isLive,
            bbox,
            providers: lightningLayer?.state.providers ?? [],
            timespan,
            disabled: !lightningLayer,
        });
    }, [isLive, toHash(bbox), toHash(lightningLayer?.state.providers), timespan]);

    return (
        <LightningServiceContext.Provider
            value={{
                setMap: (map: mapboxgl.Map) => {
                    setState((state) => ({ ...state, map }));
                },
                shuffle: (shuffleIndex: string) => {
                    setState((state) => ({ ...state, shuffleIndex }));
                },
            }}
        >
            <style>
                {`
                .pop-circle {
                    position: absolute;
                    top: 50%;
                    left: 50%;
                    width: 23px;          
                    height: 23px;
                    margin-left: 0.5px;
                    margin-top: 0.5px;
                    border: 3px solid rgba(255,69,0,0.9);
                    border-radius: 50%;
                    background: rgba(255,69,0,0.2);
                    box-shadow: 0 0 6px rgba(255,69,0,0.8);
                    transform: translate(-50%, -50%) scale(0);
                    animation: pop-scale 1.2s ease-out forwards;
                }

                @keyframes pop-scale {
                    0% {
                        transform: translate(-50%, -50%) scale(0);
                        opacity: 1;
                    }
                    50% {
                        transform: translate(-50%, -50%) scale(1.8);
                        opacity: 0.6;
                    }
                    100% {
                        transform: translate(-50%, -50%) scale(2.2);
                        opacity: 0;
                    }
                }
            `}
            </style>

            {process?.env?.IS_PLAYGROUND && (
                <div
                    style={{
                        position: 'relative',
                    }}
                >
                    <div
                        style={{
                            top: 10,
                            left: 10,
                            position: 'absolute',
                            background: 'white',
                            zIndex: 999,
                            display: 'flex',
                            justifyContent: 'center',
                            alignItems: 'center',
                            padding: '10px',
                            fontWeight: 'bold',
                            borderRadius: '5px',
                        }}
                    >
                        <p style={{ margin: 0 }}>Total strikes received: {totalStrikesReceived}</p>
                    </div>
                </div>
            )}

            {children}
        </LightningServiceContext.Provider>
    );
};
